/**
 * OpenUI TaskViews
 *
 * Live Spaces task surface for generative UI — not a static card dump.
 * Reuses SpaceViewSwitcher (board / list / table / timeline / calendar / gantt)
 * so drag-to-status and click-to-modal behave like the real Space.
 *
 * Chrome: flat compact embed. Create (+) and OpenUI Mutations refresh the board.
 */

'use client'

import { useCallback, useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { SpaceViewSwitcher } from '@/components/spaces/views/space-view-switcher'
import { Button } from '@/components/ui/button'
import { ChatbotUIContext } from '@/context/context'
import { createTask, getSpaceTasks } from '@/db/space-tasks'
import {
  openUIPanelClass,
  openUISubtitleClass,
  openUITitleClass
} from '@/lib/openui/chrome'
import {
  OPENUI_SPACE_TASKS_CHANGED_EVENT,
  dispatchOpenUISpaceTasksChanged,
  type OpenUISpaceTasksChangedDetail
} from '@/lib/openui/space-tasks-events'
import {
  dispatchSidekickOpenSpaceItem,
  getSidekickSpaceItemHref
} from '@/lib/sidekick/space-item-context'
import { cn } from '@/lib/utils'
import { useRouter } from 'next/navigation'
import type {
  SpaceTask,
  TaskPriority,
  TaskStatus
} from '@/types/spaces'
import type { ViewType } from '@/components/spaces/view-selector'

export type OpenUITaskViewsProps = {
  title?: string
  spaceId?: string | null
  defaultView?: ViewType
  className?: string
}

type SwitcherTask = {
  id: string
  title: string
  description?: string
  status: TaskStatus
  priority: TaskPriority
  due_date?: string
  start_date?: string
  assigned_to?: string
  assigned_to_name?: string
  assigned_to_avatar?: string
  time_estimate_minutes?: number
  time_spent_minutes?: number
  is_milestone?: boolean
  dependencies?: string[]
  tags?: string[]
  watchers?: string[]
  folder_id?: string
  sprint_id?: string
  space_id?: string
}

function toSwitcherTask(task: SpaceTask): SwitcherTask {
  const priority = (
    typeof task.priority === 'string' ? task.priority : 'medium'
  ) as TaskPriority

  return {
    id: task.id,
    title: task.title || 'Untitled task',
    description:
      typeof task.description === 'string' ? task.description : undefined,
    status: (task.status || 'todo') as TaskStatus,
    priority:
      priority === 'low' ||
      priority === 'medium' ||
      priority === 'high' ||
      priority === 'urgent'
        ? priority
        : 'medium',
    due_date: task.due_date || undefined,
    start_date: task.start_date || undefined,
    assigned_to: task.assigned_to || undefined,
    tags: Array.isArray(task.tags) ? task.tags : undefined,
    watchers: Array.isArray(task.watchers) ? task.watchers : undefined,
    folder_id: task.folder_id || undefined,
    sprint_id: task.sprint_id || undefined,
    space_id: task.space_id
  }
}

export function OpenUITaskViews({
  title = 'Tasks',
  spaceId: spaceIdProp,
  defaultView = 'board',
  className
}: OpenUITaskViewsProps) {
  const router = useRouter()
  const { selectedSpace, profile } = useContext(ChatbotUIContext)
  const spaceId = (spaceIdProp || selectedSpace?.id || '').trim()
  const userId = profile?.user_id?.trim() || ''
  const [tasks, setTasks] = useState<SwitcherTask[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const refresh = useCallback(async () => {
    if (!spaceId) {
      setTasks([])
      setError('Select a Space to load live tasks.')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const rows = await getSpaceTasks(spaceId, undefined, undefined, false, true)
      setTasks((rows || []).map(toSwitcherTask))
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not load tasks'
      setError(message)
      setTasks([])
    } finally {
      setLoading(false)
    }
  }, [spaceId])

  useEffect(() => {
    void refresh()
  }, [refresh, reloadKey])

  useEffect(() => {
    if (!spaceId) return

    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<OpenUISpaceTasksChangedDetail>).detail
      if (detail?.spaceId && detail.spaceId !== spaceId) return
      setReloadKey(key => key + 1)
    }

    window.addEventListener(OPENUI_SPACE_TASKS_CHANGED_EVENT, onChanged)
    return () => {
      window.removeEventListener(OPENUI_SPACE_TASKS_CHANGED_EVENT, onChanged)
    }
  }, [spaceId])

  const openTask = useCallback(
    (task: SwitcherTask) => {
      const resolvedSpaceId = task.space_id || spaceId
      if (!resolvedSpaceId) {
        toast.error('No Space linked to this task')
        return
      }
      const item = {
        spaceId: resolvedSpaceId,
        type: 'task' as const,
        id: task.id,
        title: task.title,
        summary: task.description || null
      }
      // Same resilience as message-markdown: modal first, navigate if unhandled
      // (split mode, OS embed, cross-space on /spaces/*).
      const wasHandled = dispatchSidekickOpenSpaceItem(item)
      if (!wasHandled) {
        router.push(getSidekickSpaceItemHref(item))
      }
    },
    [router, spaceId]
  )

  const handleTaskUpdate = useCallback((task: SwitcherTask) => {
    setTasks(prev => prev.map(row => (row.id === task.id ? { ...row, ...task } : row)))
  }, [])

  const handleCreateTask = useCallback(
    async (status?: string) => {
      if (!spaceId || creating) return
      if (!userId) {
        toast.error('Sign in to create a task')
        return
      }
      setCreating(true)
      try {
        const created = await createTask({
          user_id: userId,
          space_id: spaceId,
          title: 'Untitled task',
          status: (status || 'todo') as TaskStatus,
          priority: 'medium'
        })
        const row = toSwitcherTask(created)
        setTasks(prev => [row, ...prev.filter(task => task.id !== row.id)])
        dispatchOpenUISpaceTasksChanged({ spaceId, reason: 'create' })
        openTask(row)
        toast.success('Task created')
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Could not create task'
        )
      } finally {
        setCreating(false)
      }
    },
    [creating, openTask, spaceId, userId]
  )

  if (!spaceId) {
    return (
      <div className={cn('px-1 py-3', openUISubtitleClass, className)}>
        Open or select a Space first — TaskViews loads the real board from that Space.
      </div>
    )
  }

  return (
    <section
      className={cn(openUIPanelClass, className)}
      data-openui-task-views="true"
    >
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="min-w-0">
          <h4 className={openUITitleClass}>{title}</h4>
          <p className={openUISubtitleClass}>
            Drag to update · click to open · + to create
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => void handleCreateTask('todo')}
            disabled={creating || loading}
          >
            New
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setReloadKey(key => key + 1)}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <p className="px-1 text-[12px] text-destructive">{error}</p>
      ) : null}

      {loading && tasks.length === 0 ? (
        <div className="h-40 animate-pulse rounded-lg bg-foreground/[0.04]" aria-hidden="true" />
      ) : (
        <div className="max-h-[min(70vh,640px)] overflow-auto">
          <SpaceViewSwitcher
            spaceId={spaceId}
            tasks={tasks}
            defaultView={defaultView}
            compact
            onTaskClick={openTask}
            onTaskUpdate={handleTaskUpdate}
            onCreateTask={status => {
              void handleCreateTask(status)
            }}
          />
        </div>
      )}
    </section>
  )
}
